/* eslint-disable no-unused-vars */
/* React */
import React, { useContext, useMemo, useState } from 'react';

/* Router */
import { useRouter } from 'routes/index';

/* Translation */
import { useTranslation } from 'react-i18next';

/* Views */
import AddDirectorView from './AddDirector/AddDirector';

/* Components */
import { Checkbox, Modal } from 'lego-dist';
import DirectorList from './DirectorList';
import MoveDirector from './MoveDirector/MoveDirector';
import ResultActionModal from 'components/shared/ResultActionModal/ResultActionModal';
import RenderIf from 'components/RenderIf';
import RendererIf from 'components/RendererIf';

/* Styled Components */
import { CenterTab } from './styles/CenterGeneral';
import {
  DirectorHeader,
  DirectorCount,
  DirectorActions,
  SearchBar,
  SearchIcon,
  AddDirectorBtnMobile,
  AddDirectorBtn,
  StylesImageModalResultsAddedMember,
} from './styles/DirectorTab';
import SelectAllCheckbox from '../sharedComponents/styles/SelectAllCheckbox';

/* Contexts */
import { AppContext } from 'App';

/* Hooks */
import useDirector from 'hooks/UseDirector';
import useCurrentUser from 'hooks/useCurrentUser';

/* Services, Constants, Utils */
import { debounce } from 'lib/utils';
import { ActionPermission } from 'lib/specialPermissions';
import { APP_SECTIONS_PATHS } from 'constants/routes';
import { ROLES_OBJECT } from 'constants/onBoarding';

/* Icons, Images */
import memberAddedErrorImage from 'images/error.svg';
import memberAddedSuccessImage from 'images/checked.svg';

/* Themes */
import themes from 'themes';

interface DirectorTabProps {
  directors?: any;
  refreshData?(): void;
  center?: any;
}

const DirectorTab: React.FC<DirectorTabProps> = ({
  directors = [],
  refreshData = () => {},
  center: currentCenter,
}) => {
  const { t: translate, i18n } = useTranslation();
  const {
    can,
  } = useCurrentUser();
  const {
    history,
    currentParamsRef: { current: { centerId } } } = useRouter();
  const { isFreemiumUser, isDemoUser } = useContext(AppContext);
  const [filteredDirectors, setFilteredDirectors] = useState<any[]>([]);
  const [selectedDirectors, setSelectedDirectors] = useState<any[]>([]);
  const { addDirector, moveDirectors } = useDirector();

  useMemo(() => setFilteredDirectors(directors), [directors]);  // directors dep was removed due some issues in performance

  /* open modals */
  const [openAddDirectorModal, setOpenAddDirectorModal] = useState(false);
  const [openMoveDirectorsModal, setOpenMoveDirectorsModal] = useState(false);
  const [showResultActionModal, setShowResultActionModal] = useState<any>(false);
  const [showResultActionMoveDirectorModal, setShowResultActionMoveDirectorModal] = useState<any>(false);

  const ConfigResultActionModal = {
    success: {
      title: translate(
        'settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.MODAL_RESULT.DIRECTOR.TITLE',
      ),
      image: memberAddedSuccessImage,
      configPopsModal: {
        showCloseButton: false,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
    error: {
      title: translate('settings:ERROR'),
      image: memberAddedErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
  };
  const ConfigResultActionMoveDirectorModal = {
    success: {
      title: translate(
        'settings:CENTER_SECTION.MOVE_DIRECTOR.MODAL_RESULT.SUCCESS_TITLE',
      ),
      image: memberAddedSuccessImage,
      configPopsModal: {
        showCloseButton: false,
        onClosedModal: () => {
          if (setShowResultActionMoveDirectorModal) setShowResultActionMoveDirectorModal(false);
          if (refreshData) refreshData();
        },
      },
    },
    error: {
      title: translate('settings:ERROR'),
      image: memberAddedErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionMoveDirectorModal) setShowResultActionMoveDirectorModal(false);
        },
      },
    },
  };

  const [statusResultActionModal, setStatusResultActionModal] = useState<any>(
    ConfigResultActionModal.error,
  );
  const [statusResultActionMoveDirectorModal, setStatusResultActionMoveDirectorModal] = useState<any>(
    ConfigResultActionMoveDirectorModal.success
  );

  /* ADD TEACHER, MOVE TEACHER functions */
  const handleAddDirectorFlow = () => {
    if (isFreemiumUser || isDemoUser) {
      history.push(APP_SECTIONS_PATHS.PREMIUM_PROCESS);
    } else {
      setOpenAddDirectorModal(true);
    }
  };

  const handleOnFinishFlow = (values: any) => {
    const { general_info, permissions, center } = values;

    const form = {
      user: {
        type: ROLES_OBJECT.DIRECTOR.value,
        title: ROLES_OBJECT.DIRECTOR.value,
        role_alias: permissions.role_alias,
        name: general_info.name,
        lastname: general_info.lastname,
        center_ids: center?.centers?.map((c: any) => typeof(c) === "number" ? c : c?.id) || [],
        email: general_info.email,
        locale: i18n.language,
        permission_ids: permissions.permission_ids,
      },
    };
    addDirector(centerId, form)
      .then(({user}:any) => {
        if (user?.id && refreshData) {
          refreshData();
        }
        setStatusResultActionModal(
          user?.id
            ? ConfigResultActionModal.success
            : ConfigResultActionModal.error,
        );
      })
      .catch(() => setStatusResultActionModal(ConfigResultActionModal.error))
      .finally(() => {
        setOpenAddDirectorModal(false);
        setShowResultActionModal(true);
      });
  };

  /* SEARCH functions */
  const handleSearch = debounce((value: any) => {
    const pattern = new RegExp(`${value}`, 'i');
    const filterResult = directors.filter(
      (teacher: any) => teacher.name && pattern.test(teacher.name),
    );
    setFilteredDirectors(filterResult);
  }, 300);

  const handleSelectAllDirectors = (checked: boolean) => {
    if(checked) {
      setSelectedDirectors(filteredDirectors);
    } else {
      setSelectedDirectors([]);
    }
  };

  const toggleSelectDirector = (director: any) => {
    let currentSelected = [...selectedDirectors];

    if(currentSelected.find((item: any) => item.id === director.id)) {
      currentSelected = currentSelected.filter((item: any) => item.id !== director.id)
    } else {
      currentSelected.push(director);
    }
    setSelectedDirectors(currentSelected);
  }

  const handleOnClickMoveDirector = (centersSelected: any) => {
    setOpenMoveDirectorsModal(false);

    moveDirectors({
      directors: selectedDirectors.map((director: any) => director.id),
      centers: centersSelected.map((center: any) => center.id),
      centerId: currentCenter?.id
    }).then((response: any) => {
      setStatusResultActionMoveDirectorModal(ConfigResultActionMoveDirectorModal[response?.isAxiosError ? 'error': 'success']);
    }).catch((error: any) => {
      setStatusResultActionMoveDirectorModal(ConfigResultActionMoveDirectorModal.error);
    }).finally(() => setShowResultActionMoveDirectorModal(true));
  }

  const hasAllChecked = useMemo(() => {
    return selectedDirectors.length === filteredDirectors.length
  }, [selectedDirectors, filteredDirectors]);

  const hasSomeItemsChecked = useMemo(() => {
    const hasCheckedAtLeastAnItem = selectedDirectors.length > 0;

    return hasCheckedAtLeastAnItem && !hasAllChecked;
  }, [selectedDirectors, hasAllChecked]);

  const handleOnDirectorDeleted = () => {
    if(refreshData) refreshData();
  }

  return (
    <CenterTab>

      <RendererIf condition={showResultActionModal}>
        <ResultActionModal
          styles={{ Image: StylesImageModalResultsAddedMember }}
          {...statusResultActionModal}
        />
      </RendererIf>
      <RendererIf condition={showResultActionMoveDirectorModal}>
        <ResultActionModal
          styles={{ Image: StylesImageModalResultsAddedMember }}
          {...statusResultActionMoveDirectorModal}
        />
      </RendererIf>

      <DirectorHeader>
        <DirectorCount>
          <RendererIf condition={can(ActionPermission.MOVE_DIRECTORS_FROM_CENTER)} >
            <SelectAllCheckbox someChecked={hasSomeItemsChecked}>
              <Checkbox
                label=" "
                name="permissions"
                checked={hasAllChecked}
                onChange={(checked: boolean) => handleSelectAllDirectors(checked)}
              />
            </SelectAllCheckbox>
          </RendererIf>
          {translate('settings:DIRECTORS')} ({directors.length})
        </DirectorCount>
        <DirectorActions>
          <RendererIf condition={!selectedDirectors?.length}
            alternative={
              <RendererIf condition={can(ActionPermission.MOVE_DIRECTORS_FROM_CENTER)}>
                <AddDirectorBtn
                  rounded
                  flat
                  fill
                  color={themes.colors.newKinedu}
                  onClick={() => setOpenMoveDirectorsModal(true)}
                >
                  {translate('MOVE_TO')}
                </AddDirectorBtn>
              </RendererIf>
          }>
            <>
              <SearchBar
                theme="Rounded"
                placeholder={translate('settings:STAFF_SECTION.MANAGE.SEARCH')}
                onChange={handleSearch}
                rightSlot={() => <SearchIcon />}
              />

              <RenderIf condition={can(ActionPermission.ADD_DIRECTORS)}>
                <>
                  <AddDirectorBtnMobile
                    rounded
                    flat
                    fill
                    color="#75B753"
                    onClick={() => handleAddDirectorFlow()}
                  >
                    +
                  </AddDirectorBtnMobile>
                  <AddDirectorBtn
                    rounded
                    flat
                    fill
                    color="#75B753"
                    onClick={() => handleAddDirectorFlow()}
                  >
                    {translate('settings:ADD_DIRECTOR')}
                  </AddDirectorBtn>
                </>
              </RenderIf>
            </>
          </RendererIf>
        </DirectorActions>
      </DirectorHeader>
      <DirectorList
        toggleSelectDirector={toggleSelectDirector}
        selectedDirectors={selectedDirectors}
        directors={filteredDirectors.length > 0 ? filteredDirectors : directors}
        handleOnDirectorDeleted={handleOnDirectorDeleted}
      />

      <Modal
        openModal={openAddDirectorModal}
        title={translate('settings:ADD_DIRECTOR')}
        headerColor={themes.colors.newKinedu}
        closeButtonColor="#ffffff"
        confirmCloseModal={true}
        confirmCloseModalTexts={{
          title: translate('activities:IF_YOU_LEAVE.TITLE'),
          button_leave: translate('activities:IF_YOU_LEAVE.BUTTON_LEAVE'),
          button_stay: translate('activities:IF_YOU_LEAVE.BUTTON_STAY'),
          description: translate('activities:IF_YOU_LEAVE.DESCRIPTION'),
          warning: translate('activities:IF_YOU_LEAVE.WARNING')
        }}
        persistent
        onClosedModal={() => {
          setOpenAddDirectorModal(false);
        }}
      >
        {() => <AddDirectorView onFinishFlow={handleOnFinishFlow} />}
      </Modal>
      <Modal
        openModal={openMoveDirectorsModal}
        headerColor={themes.colors.newKinedu}
        title={translate('settings:CENTER_SECTION.MOVE_DIRECTOR.TITLE')}
        closeButtonColor="#fff"
        onClosedModal={() => {
          setOpenMoveDirectorsModal(false);
        }}
      >
        {() => <MoveDirector handleOnClickMoveDirector={handleOnClickMoveDirector} />}
      </Modal>
    </CenterTab>
  );
};

export default DirectorTab;
